import styled from 'styled-components';
import { useEffect, useState } from 'react';
import {
	Card,
	Divider,
	Pagination,
	Button,
	Modal,
	Form,
	Input,
	message,
	Select,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import remakeQueryString from '../../methods/utils/remake-query-string';
import {
	createCommercialBank,
	getCommercialBanks,
} from '../../methods/redux/reducers/commercialBank';
import CommercialBankItem from './commercial-bank-item';
import { getCurrencies } from '../../methods/redux/reducers/currency';

export const TableHeader = styled.th`
	padding: 12px 20px;
	text-align: left;
	font-weight: 800;
	color: #262626;
	border-bottom: 1px solid #f0f0f0;
	background-color: #f8f8f8;
	margin-bottom: 10px;
	display: grid;
	justify-content: space-between;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 100px;
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const CommercialBanks = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [refresh, setRefresh] = useState(0);
	const [form] = Form.useForm();

	const { currencies } = useAppSelector((store) => store.currencies);
	const { commercialBanks } = useAppSelector((store) => store.commercialBank);
	const [isLoadingCreateJob, setIsLoadingCreateJob] = useState(false);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(commercialBanks.paginator.perPage) || '10',
			});
		} else {
			dispatch(getCommercialBanks(queryData));
		}

		dispatch(getCurrencies({}));
	}, [searchParams, dispatch, commercialBanks.paginator.perPage, refresh]);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const currencySelect = currencies
		.filter((item: any) => item.currencyForm === 'fiat')
		.map((item: any) => ({
			label: item.symbol,
			value: item._id,
		}));

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const handleSubmit = async (values: any) => {
		try {
			setIsLoadingCreateJob(true);

			const jobData = {
				name: values.name,
				symbol: values.symbol,
				nibssCode: values.nibssCode,
				sortCode: values.sortCode,
				localCurrencyId: values.localCurrencyId,
				address: values.address,
				countryCode: values.countryCode,
				apiTokenKeyInENV: 'string',
			};

			await dispatch(createCommercialBank(jobData));
			handleCancel();
			form.resetFields();
			setRefresh(Math.random());
		} catch (error) {
			console.info('errpr', error);

			message.error('Cnat create');
		} finally {
			setIsLoadingCreateJob(false);
		}
	};

	return (
		<Card>
			<h1>Commercial Banks</h1>
			<p>Track and manage commercial banks.</p>
			<Button
				type="primary"
				onClick={showModal}
				style={{ marginTop: '16px', marginBottom: '16px' }}
			>
				Create Commercial Bank
			</Button>
			<Divider />
			<p>
				Total of <strong>{commercialBanks?.paginator?.itemCount} items</strong>{' '}
				found
			</p>
			<Divider />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={commercialBanks.paginator.itemCount}
				current={commercialBanks.paginator.currentPage}
				pageSize={commercialBanks.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
			<br />
			<TableHeader>
				<div>Name</div>
				<div>Local Currency</div>
				<div>Country Code</div>
				<div>Nibss Code</div>
				<div>Address</div>
				<div>Created At</div>
				<div>Status</div>
				<div>Actions</div>
			</TableHeader>
			{commercialBanks?.itemsList?.length > 0 &&
				commercialBanks?.itemsList.map((item: any) => (
					<CommercialBankItem
						key={item._id}
						item={item}
						setRefresh={setRefresh}
						currencySelect={currencySelect}
					/>
				))}
			<br />
			<Divider />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={commercialBanks.paginator.itemCount}
				current={commercialBanks.paginator.currentPage}
				pageSize={commercialBanks.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
			{/* Modal for Creating a Commercial Bank */}
			<Modal
				title="Create Commercial Bank"
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<StyledFormItem
						name="name"
						label="Bank name"
						rules={[{ required: true, message: 'Please input the bank name!' }]}
					>
						<Input placeholder="e.g., United Bank for Africa" />
					</StyledFormItem>
					<StyledFormItem
						name="symbol"
						label="Bank symbol"
						rules={[
							{ required: true, message: 'Please input the bank symbol!' },
						]}
					>
						<Input placeholder="e.g., UBA" />
					</StyledFormItem>
					<StyledFormItem
						name="nibssCode"
						label="Nibss Code"
						rules={[
							{ required: true, message: 'Please input the Nibss code!' },
						]}
					>
						<Input placeholder="e.g., 222" />
					</StyledFormItem>
					<StyledFormItem
						name="sortCode"
						label="Sort Code"
						rules={[{ required: true, message: 'Please input the sortCode!' }]}
					>
						<Input placeholder="e.g., 222" />
					</StyledFormItem>
					<StyledFormItem
						name="localCurrencyId"
						label="Local Currency Id"
						rules={[
							{ required: true, message: 'Please input the localCurrencyId!' },
						]}
					>
						<Select placeholder="Select a currency" options={currencySelect} />
					</StyledFormItem>
					<StyledFormItem
						name="address"
						label="Address"
						rules={[
							{ required: true, message: 'Please input the bank address!' },
						]}
					>
						<Input placeholder="e.g., Nigeria" />
					</StyledFormItem>
					<StyledFormItem
						name="countryCode"
						label="Country Code"
						rules={[
							{ required: true, message: 'Please input a country code!' },
						]}
					>
						<Input placeholder="e.g., NG" />
					</StyledFormItem>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoadingCreateJob}
						>
							{isLoadingCreateJob ? 'Creating...' : 'Create Commercial Bank'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default CommercialBanks;
